import { useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { LinkPickerPlugin } from '@atlaskit/link-picker';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type AtlassianLinkPickerPluginConfig,
	Scope,
	useAtlassianPlugins,
} from '@atlassian/link-picker-atlassian-plugin';

import {
	isSupportedCurrentProduct,
	lookupCurrentProductByProduct,
	type SupportedCurrentProduct,
	supportedCurrentProducts,
} from './types';
import { useAvailableProducts } from './utils';

export const JIRA_PLUGIN_BASE_CONFIG: AtlassianLinkPickerPluginConfig = {
	tabConfig: {
		tabKey: 'jira',
		tabTitle: 'Jira',
	},
	products: ['jira'],
	scope: Scope.JiraIssueDashboardBoardProjectFilter,
};

export const JIRA_PLUGIN_CREATE_CONFIG: AtlassianLinkPickerPluginConfig = {
	...JIRA_PLUGIN_BASE_CONFIG,
	action: {
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
		label: 'Create',
		callback: () => {
			console.log('Placeholder for Jira Create');
		},
	},
};

export const CONFLUENCE_PLUGIN_BASE_CONFIG: AtlassianLinkPickerPluginConfig = {
	tabConfig: {
		tabKey: 'confluence',
		tabTitle: 'Confluence',
	},
	products: ['confluence'],
	scope: Scope.ConfluenceContentType,
};

export const ATLAS_PLUGIN_BASE_CONFIG: AtlassianLinkPickerPluginConfig = {
	tabConfig: {
		tabKey: 'atlas',
		tabTitle: 'Atlas',
	},
	products: ['townsquare'],
	scope: Scope.TownsquareProject,
};

export const TOWNSQUARE_PLUGIN_BASE_CONFIG: AtlassianLinkPickerPluginConfig = {
	tabConfig: {
		tabKey: 'home',
		tabTitle: 'Home',
	},
	products: ['townsquare'],
	scope: Scope.TownsquareProject,
};

export const LOOM_PLUGIN_BASE_CONFIG: AtlassianLinkPickerPluginConfig = {
	tabConfig: {
		tabKey: 'loom',
		tabTitle: 'Loom',
	},
	products: ['loom'],
	scope: Scope.Loom,
	disableActivityClient: true,
};

const currentProductToPluginMap: Record<SupportedCurrentProduct, AtlassianLinkPickerPluginConfig> =
	{
		jira: JIRA_PLUGIN_BASE_CONFIG,
		confluence: CONFLUENCE_PLUGIN_BASE_CONFIG,
		atlas: ATLAS_PLUGIN_BASE_CONFIG,
		loom: LOOM_PLUGIN_BASE_CONFIG,
		townsquare: TOWNSQUARE_PLUGIN_BASE_CONFIG,
	};

export interface LinkPickerPluginsConfiguration {
	/**
	 * The endpoint of the activity client.
	 */
	activityClientEndpoint?: string;

	/**
	 * The URL of the agreggator for search provider.
	 */
	aggregatorUrl?: string;
	/** The current cloud ID, used to configure search */
	cloudId?: string;
	/**
	 * The current product. Should be 'jira', 'confluence', or 'atlas' (not `townsquare`).
	 * Note that it is not 'jira-software' or `something.ondemand`.
	 *
	 * An unknown current product will not ensure the first tab is the current product
	 *
	 * See getProductConfig.
	 */
	product?: string;
}

/**
 * Returns configuration to determine what plugins should appear for atlassian related
 */
export const useProductConfig = (
	config?: LinkPickerPluginsConfiguration,
): {
	atlassianPluginsConfig: AtlassianLinkPickerPluginConfig[];
	isLoading: boolean;
} => {
	const { cloudId, activityClientEndpoint, aggregatorUrl } = config ?? {};
	const { availableProducts, isLoading } = useAvailableProducts(config?.cloudId);

	/**
	 * Lower case possible name of product. Should be `confluence` or `jira`.
	 * This typing is not ideal. An unknown current product will:
	 * - in the case of no 1P tabs enabled, default to Scope.ConfluencePageBlog scope
	 * - in the case of 1P tabs enabled, not ensure the first tab is the current product
	 */
	const currentProduct = config?.product?.toLowerCase();

	const createPluginConfig = useMemo(() => {
		return (config: Partial<AtlassianLinkPickerPluginConfig>): AtlassianLinkPickerPluginConfig => ({
			cloudId,
			activityClientEndpoint,
			aggregatorUrl,
			...config,
		});
	}, [cloudId, activityClientEndpoint, aggregatorUrl]);

	const configMap: Record<SupportedCurrentProduct, AtlassianLinkPickerPluginConfig> =
		useMemo(() => {
			return {
				jira: createPluginConfig(
					fg('platform.linking-platform.link-picker.enable-jira-create')
						? JIRA_PLUGIN_CREATE_CONFIG
						: currentProductToPluginMap.jira,
				),
				confluence: createPluginConfig(currentProductToPluginMap.confluence),
				atlas: createPluginConfig(currentProductToPluginMap.atlas),
				townsquare: createPluginConfig(currentProductToPluginMap.townsquare),
				loom: createPluginConfig(currentProductToPluginMap.loom),
			};
		}, [createPluginConfig]);

	return useMemo(() => {
		const plugins: AtlassianLinkPickerPluginConfig[] = [];
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const isLoomBetaEnabled = FeatureGates.checkGate('confluence_loom_beta_enabled');
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const isLoomM25Enabled = FeatureGates.checkGate('confluence_frontend_loom_cross_sell');

		// Add plugin for current product as the first tab, if we have a supported tab for it
		// We are assuming that if a product is the current product and we have a supported tab, it's in available products
		// (eg. we assume if you're in Jira, Jira will be in available products)
		if (currentProduct && isSupportedCurrentProduct(currentProduct)) {
			plugins.push(configMap[currentProduct]);
		}

		return {
			isLoading,
			atlassianPluginsConfig: [
				...plugins,
				...supportedCurrentProducts
					// Exclude the current product, we've already included it above if theres a matching config available
					.filter((product) => currentProduct !== product)
					// Exclude 'Atlas' tab is there is already a 'Home' tab
					.filter((product) => {
						if (currentProduct === 'townsquare') {
							return product !== 'atlas';
						}
						return !!product;
					})
					// Remove any products that did not appear in the list of availableProducts
					// Does not factor in the currentProduct, that will still be in the resulting array as it
					// has been included seperately above
					.filter(
						(product) =>
							(product === 'loom' && isLoomBetaEnabled) ||
							availableProducts.some(
								(availableProduct) =>
									product === lookupCurrentProductByProduct(availableProduct) &&
									(product !== 'loom' || isLoomM25Enabled),
							),
					)
					// Map product strings to their defined config in the config map
					.map((product) => configMap[product]),
			],
		};
	}, [configMap, availableProducts, currentProduct, isLoading]);
};

export const useLinkPickerPlugins = (
	config: LinkPickerPluginsConfiguration,
): { isLoading: boolean; plugins: LinkPickerPlugin[] } => {
	const { atlassianPluginsConfig, isLoading } = useProductConfig(config);
	const plugins = useAtlassianPlugins(atlassianPluginsConfig);

	return { plugins, isLoading };
};
